'use client';
import { getNetworkFromChainSlug } from '@/domain/network';
import { InfrastructureProvider } from '@/presentation/context/infrastructure';
import { NetworkProvider } from '@/presentation/context/network';
import NftxProvider from '@/presentation/context/nftx';
import { SettingsProvider } from '@/presentation/context/settings';
import { Web3Provider } from '@/presentation/context/web3';
import { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ModalProvider } from '@/presentation/context/modal';
import { IntlProvider } from 'use-intl';
import en from 'messages/en.json';
import { Permit2Provider } from '@/presentation/context/permit2';
import { SocketSyncProvider } from '@/presentation/context/socketSync';
import { AlertProvider } from '@/presentation/context/alert';
import { ThemeProvider } from '@/presentation/context/theme';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      keepPreviousData: true,
      staleTime: 1000 * 60 * 2.5,
      refetchInterval: 1000 * 60 * 5,
      refetchOnWindowFocus: true,
      onError: (e) => {
        console.error(e);
      },
      queryKeyHashFn: (queryKey) => {
        return JSON.stringify(queryKey, (_, val) => {
          if (typeof val === 'bigint') {
            return `${val}`;
          }
          if (val && typeof val === 'object') {
            return Object.keys(val)
              .sort()
              .reduce((result, key) => {
                result[key] = val[key];
                return result;
              }, {} as any);
          }
          return val;
        });
      },
      retry: (x, y: any) => {
        if (x >= 3) {
          // Only retry 3 times
          return false;
        }
        if (
          y?.status &&
          typeof y.status === 'number' &&
          y.status >= 400 &&
          y.status < 500
        ) {
          // It's a 4xx error caused by bad inputs from the user
          // Don't keep retrying the request
          return false;
        }
        return true;
      },
    },
  },
});

export default function Providers({
  chainSlug,
  children,
}: {
  chainSlug: string;
  children: ReactNode;
}) {
  const network = getNetworkFromChainSlug(chainSlug);

  return (
    <IntlProvider locale="en" messages={en} timeZone="UTC">
      <QueryClientProvider client={queryClient}>
        <NetworkProvider slug={chainSlug}>
          <InfrastructureProvider>
            <SettingsProvider>
              <ModalProvider>
                <Web3Provider network={network}>
                  <NftxProvider network={network}>
                    <SocketSyncProvider>
                      <Permit2Provider>
                        <AlertProvider>
                          <ThemeProvider
                            attribute="class"
                            defaultTheme="dark"
                            enableSystem
                            disableTransitionOnChange
                          >
                            {children}
                          </ThemeProvider>
                        </AlertProvider>
                      </Permit2Provider>
                    </SocketSyncProvider>
                  </NftxProvider>
                </Web3Provider>
              </ModalProvider>
            </SettingsProvider>
          </InfrastructureProvider>
        </NetworkProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </IntlProvider>
  );
}
